import Shaka from 'shaka-player/dist/shaka-player.ui'

import EventEmitter from './event_emitter'
import NekoSeekBar from './neko_seek_bar'
import MultiViewPointsMenu from './multi_view_points_menu'
import ManifestMenu from './manifest_menu'
import FullscreenMenuButton from './fullscreen_menu_button'

class NekoPlayer extends EventEmitter {
  /**
   * @param {HTMLElement} container
   * @param {NekoPlayer.DataSource} datasource
   * @param {Boolean} apiEnabled
   */
  constructor(container, datasource, apiEnabled = false) {
    super()

    /** @private {!HTMLElement} */
    this._container = container

    /** @private {NekoPlayer.DataSource} */
    this._datasource = datasource

    /** @private {HTMLElement} */
    this._posterImage = null

    /** @private {HTMLElement} */
    this._video = null

    /** @private {HTMLElement} */
    this._errorMessage = null

    /** @private {Shaka.Player} */
    this._player = null

    /** @private {Shaka.offline.Storage} */
    this._storage = null

    /** @private {Shaka.ui.Overlay} */
    this._overlay = null

    /** @private {Boolean} ブラウザがフルスクリーンに対応しない場合に利用 */
    this._isFullscreen = false

    /** @private {Boolean} */
    this._apiEnabled = apiEnabled
  }

  get container() { return this._container }
  get mediaElement() { return this._player.getMediaElement() }

  get isPausing() { return this._video.paused }
  get isFinished() { return this._video.ended }
  get currentTime() { return this._video.currentTime }

  get isMuted() { return this._video.muted }
  set muted(enable) { this._video.muted = enable }

  set autoplay(enable) { this._video.autoplay = enable }

  get assetUri() { return this._player.getAssetUri() }

  get storage() { return new NekoPlayer.Storage(this._storage) }

  /**
   * shaka.polyfill.Fullscreen が install されている前提である点に注意
   * @requires module:shaka.polyfill.Fullscreen
   */
  get isSupportFullscreen() { return document.fullscreenEnabled !== undefined }
  get isFullscreen() {
    const hasFullscreenElement = this.isSupportFullscreen && document.fullscreenElement !== null
    return hasFullscreenElement || this._isFullscreen
  }

  /**
   * @returns {boolean} browser supports shaka player
   */
  static isBrowserSupported() {
    Shaka.polyfill.installAll()
    return Shaka.Player.isBrowserSupported()
  }

  /**
   * @returns {boolean} browser supports offline play
   */
  static isOfflinePlaySupported() {
    return Shaka.offline.Storage.support()
  }

  /**
   * @async
   */
  async init() {
    if (!this._datasource) {
      return
    }

    await this.reloadData()

    const posterImage = document.createElement('img')
    posterImage.classList.add('nekojs-player-poster')
    posterImage.src = this._datasource.playerPosterImageUri(this)

    const video = document.createElement('video')
    video.classList.add('nekojs-media-play')
    video.poster = 'data:image/gif,AAAA'
    video.autoplay = true

    // [TODO] 岡山案件の hotfix 対応のため、別途リファクタリングが必要
    // 今後の方針として、URL パラメータからの再生設定も可能にし
    // 利便性を高めた方が良いか別途検討
    const url = new URL(document.location.href)
    const notplaysinline = Number(url.searchParams.get('notplaysinline')) === 1
    if (!notplaysinline) {
      video.setAttribute('playsinline', '')
    }

    video.setAttribute('preload', 'auto')

    const errorMessage = document.createElement('div')
    errorMessage.classList.add('nekojs-player-error')

    if (window.parent !== window) {
      this._container.style.height = `${window.innerHeight}px`
      this._container.style.width = `${window.innerWidth}px`
    }
    this._container.appendChild(video)
    this._container.appendChild(posterImage)
    this._container.appendChild(errorMessage)

    Shaka.ui.Controls.registerSeekBar(this._datasource.registerSeekBarElement(this))
    this._datasource.registerControlElements(this).forEach((element, key) =>
      Shaka.ui.Controls.registerElement(key, element)
    )

    const player = new Shaka.Player(video)
    player.getNetworkingEngine().registerRequestFilter(this._datasource.registerRequestFilter(this))

    const storage = new Shaka.offline.Storage(player)

    const overlay = new Shaka.ui.Overlay(player, this._container, video)
    overlay.configure({ controlPanelElements: [], addSeekBar: false })

    this._posterImage = posterImage
    this._video = video
    this._errorMessage = errorMessage
    this._player = player
    this._storage = storage
    this._overlay = overlay

    this._attachEvents()
  }

  /**
   * DataSource の再読み込み
   * @async
   */
  async reloadData() {
    this._datasource && await this._datasource.init()
  }

  /**
   * DataSource.playerContentSrc() に定義されたリソースを読み込む
   * @async
   * @param {?Number} startTime default null
   * @returns {Promise<void>}
   */
  async load(startTime = null) {
    this.trigger(NekoPlayer.Events.WillLoadManifest, this)

    const src = await this._datasource.playerContentSrc(this)
    if (!src) {
      return
    }

    // 取得したソースが mp4 の場合
    if (src.split('.').pop() === 'mp4') {
      this._video.src = src
      this._video.currentTime = startTime
      this.trigger(NekoPlayer.Events.DidLoadManifest, this, null)

      return
    }

    this._overlay.configure(this._datasource.playerUIConfigration(this))
    this._player.configure(this._datasource.playerConfigration(this))
    try {
      await this._player.load(src, startTime)
      this.trigger(NekoPlayer.Events.DidLoadManifest, this, null)
    } catch (error) {
      this.trigger(NekoPlayer.Events.DidLoadManifest, this, error)
    }
  }

  /**
   * 強制的に再生終了させる。
   * @async
   */
  async finishPlayback() {
    this.trigger(NekoPlayer.Events.DidFinishPlayback, this)
    this.unload()
    this._overlay.configure({ controlPanelElements: [], addSeekBar: false })
  }

  async unload() {
    this._player.unload()
  }

  removeSrc() {
    this._video.src = ''
  }

  /**
   * @param {string} uri
   * @param {Object<string,any>} metadata
   * @returns @returns {shaka.extern.IAbortableOperation.<shaka.extern.StoredContent>}
   */
  async download(uri, metadata) {
    this._storage.configure(this._datasource.playerConfigration(this))
    this._storage.configure(this._datasource.playerDownloadConfigration(this))

    const operation = this._storage.store(uri, metadata)
    operation.promise
      .then(storedContent => this.trigger(NekoPlayer.Events.DidFinishDownload, this, storedContent))
      .catch(error => this.trigger(NekoPlayer.Events.DidFailDownload, this, error))
  }

  start() {
    if (this.isPausing || this.isFinished) {
      this._video.play()
    }
  }

  pause() {
    if (!this.isFinished && !this.isPausing) {
      this._video.pause()
    }
  }

  /**
   * フルスクリーン移行
   * iPhone など、Element.requestFullscreen が利用できないブラウザの場合、
   * container 要素を window サイズに合わせて擬似的にフルスクリーン化する
   */
  enterFullscreen() {
    if (this.isSupportFullscreen) {
      if (!this.isFullscreen) this._container.requestFullscreen()
      return
    }

    this.trigger(NekoPlayer.Events.WillEnterFullscreen, this)
    this._container.style.height = `${window.innerHeight}px`
    this._container.style.width = `${window.innerWidth}px`

    // タッチスクロールを無効化
    document.addEventListener('touchmove', this._disableTouchScroll, { passive: false })

    this.trigger(NekoPlayer.Events.DidEnterFullscreen, this)

    this._isFullscreen = true
  }

  /**
   * フルスクリーン解除
   * iPhone など、Element.requestFullscreen が利用できないブラウザの場合、
   * NekoPlayer.enterFullscreen で変更した container の heigh, width をリセット
   */
  exitFullscreen() {
    if (this.isSupportFullscreen) {
      if (this.isFullscreen) this._container.exitFullscreen()
      return
    }

    this._container.style.height = ''
    this._container.style.width = ''

    document.removeEventListener('touchmove', this._disableTouchScroll, { passive: false })

    this.trigger(NekoPlayer.Events.DidExitFullscreen, this)

    this._isFullscreen = false
  }

  /**
   * @param {boolean} isDark
   */
  showPosterImage(isDark = false) {
    this._posterImage.style.display = ''
    this._posterImage.style.filter = isDark ? 'brightness(0.5)' : ''
  }

  hidePosterImage() {
    this._posterImage.style.display = 'none'
  }

  /**
   * @param {string} message
   */
  showErrorMessage(message) {
    this._errorMessage.innerHTML = ''

    const errorText = document.createElement('p')
    errorText.classList.add('nekojs-player-error-text')
    errorText.textContent = message

    const errorMessageCloseButton = document.createElement('p')
    errorMessageCloseButton.classList.add('nekojs-player-error-close')
    errorMessageCloseButton.addEventListener('click', () => this.hideErrorMessage())

    const button = document.createElement('a')
    button.classList.add('delete', 'is-medium')
    errorMessageCloseButton.appendChild(button)

    this._errorMessage.appendChild(errorText)
    this._errorMessage.appendChild(errorMessageCloseButton)
    this._errorMessage.style.display = 'flex'
  }

  hideErrorMessage() {
    this._errorMessage.style.display = 'none'
    this._errorMessage.innerHTML = ''
  }

  showControls() {
    const controlsContainer = this._getControlsContainer()
    if (controlsContainer) {
      controlsContainer.style.display = ''
    }
  }

  hideControls() {
    const controlsContainer = this._getControlsContainer()
    if (controlsContainer) {
      controlsContainer.style.display = 'none'
    }
  }

  /**
   * キーボードによるプレイヤー操作を行う
   * 呼び出し元で event の伝搬を制御する可能性があるため、返り値を用意
   *
   * @param {String} key event.key の中身
   * @returns {Boolean} キーに付随するイベントを実行できていたら true
   */
  executeKeyEvent(key) {
    // NekoPlayer で対応するデフォルトキーイベントの定義を以下に追記する
    const keyEvents = {
      'f': () => this.enterFullscreen(),
      'm': () => this.muted = !this.isMuted,
      ' ': () => this.isPausing ? this.start() : this.pause()
    }

    if (keyEvents.hasOwnProperty(key)) {
      keyEvents[key]()
      return true
    }

    return false
  }

  /**
   * プレイヤーの埋め込み元から PostMessage で受け取ったメッセージの実行を行う
   * @param {Object} message
   */
  executeMessage(message) {
    if (message['event'] === 'command') {
      const commandFunctions = {
        'playVideo': () => this.start(),
        'pauseVideo': () => this.pause(),
        'mute': () => this.muted = true,
        'unMute': () => this.muted = false,
        'showControls': () => this.showControls(),
        'hideControls': () => this.hideControls()
      }
      const functionName = message['func']
      // [TODO] 引数が必要な Function の場合、以下の様なメッセージが必要
      // const functionArgs = message['args']

      if (commandFunctions.hasOwnProperty(functionName)) {
        commandFunctions[functionName]()
      }
    }
  }

  /**
   * IFrame などでプレイヤーを組み込んだ際に、組み込み元の Window に対してメッセージを送出する
   * この際、URL パラメータから渡された origin の値と、受信側の origin が異なる場合は xss のエラーとなる
   * [TODO] Message のやりとりや Origin の検証などは切り出して別のクラスに担当させる予定
   * @param {string} message json string
   */
  sendMessage(message) {
    const url = new URL(document.location.href)
    const origin = url.searchParams.get('origin')
    if (this._apiEnabled && origin && window.parent !== window) {
      window.parent.postMessage(message, origin)
    }
  }

  /**
   * @returns {HTMLElement}
   * @private
   */
  _getControlsContainer() {
    const controls = this._overlay.getControls()
    return controls ? controls.getControlsContainer() : null
  }

  /**
   * @private
   */
  _attachEvents() {
    const startPlaybackEventHandler = (event) => this._startPlaybackEventHandler(event)
    this._video.addEventListener('play', startPlaybackEventHandler)

    const pausePlaybackEventHandler = (event) => this._pausePlaybackEventHandler(event)
    this._video.addEventListener('pause', pausePlaybackEventHandler)

    const finishPlaybackEventHandler = (event) => this._finishPlaybackEventHandler(event)
    this._video.addEventListener('ended', finishPlaybackEventHandler)

    const seekingEventHandler = (event) => this._seekingEventHandler(event)
    this._video.addEventListener('seeking', seekingEventHandler)

    const seekedEventHandler = (event) => this._seekedEventHandler(event)
    this._video.addEventListener('seeked', seekedEventHandler)

    const fullscreenEventHandler = (event) => this._fullscreenEventHandler(event)
    this._container.addEventListener('fullscreenchange', fullscreenEventHandler)

    const resizeWindowEventHandler = (event) => this._resizeWindowEventHandler(event)
    window.addEventListener('resize', resizeWindowEventHandler)

    const rotateWindowEventHandler = (event) => this._rotateWindowEventHandler(event)
    window.addEventListener('orientationchange', rotateWindowEventHandler)

    const closeWindowEventHandler = (event) => this._closeWindowEventHandler(event)
    window.addEventListener('beforeunload', closeWindowEventHandler)

    const keyDownEventHandler = (event) => this._keyDownEventHandler(event)
    window.addEventListener('keydown', keyDownEventHandler)

    if (this._apiEnabled) {
      const receiveMessageEventHandler = (event) => this._receiveMessageEventHandler(event)
      window.addEventListener('message', receiveMessageEventHandler)
    }
  }

  /**
   * @param {Event} event
   * @private
   */
  _startPlaybackEventHandler(event) {
    this.trigger(NekoPlayer.Events.DidStartPlayback, this)
    this._video.style.filter = ''
  }

  /**
   * @param {Event} event
   * @private
   */
  _pausePlaybackEventHandler(event) {
    this.trigger(NekoPlayer.Events.DidPausePlayback, this)
  }

  /**
   * @param {Event} event
   * @private
   */
  _seekingEventHandler(event) {
    this.trigger(NekoPlayer.Events.DidBeginSeeking, this)
  }

  /**
   * @param {Event} event
   * @private
   */
  _seekedEventHandler(event) {
    this.trigger(NekoPlayer.Events.DidEndSeeking, this)
  }

  /**
   * @param {Event} event
   * @private
   */
  _finishPlaybackEventHandler(event) {
    this.trigger(NekoPlayer.Events.DidFinishPlayback, this)

    // PlayReady の場合、再生後の video 要素に対して filter を変更すると、
    // DRM 保護が働くのか、画面が真っ黒になってしまうため、コメントアウトしている。
    // this._video.style.filter = 'brightness(0.5)'
  }

  /**
   * @param {Event} event
   * @private
   */
  _closeWindowEventHandler(event) {
    this.trigger(NekoPlayer.Events.WillCloseWindow, this)
  }

  /**
   * @param {Event} event
   * @private
   */
  _resizeWindowEventHandler(event) {
    if (!this.isSupportFullscreen && this.isFullscreen) {
      // iPhone などブラウザがフルスクリーン非対応時
      this._container.style.height = `${window.innerHeight}px`
      this._container.style.width = `${window.innerWidth}px`

      // ツールバーの表示・非表示で位置ずれが起きるため、位置調整
      window.scrollTo(0, 0)
    }

    this.trigger(NekoPlayer.Events.DidResizeWindow, this)
  }

  /**
   * @param {Event} event
   * @private
   */
  _rotateWindowEventHandler(event) {
    let angle = screen && screen.orientation && screen.orientation.angle
    if (angle === undefined) angle = window.orientation // iOS 向け
    this.trigger(NekoPlayer.Events.DidRotateWindow, this, angle)
  }

  /**
   * @param {Event} event
   * @private
   */
  _fullscreenEventHandler(event) {
    if (this.isFullscreen) {
      this._container.style.width = '100%'
      this._container.style.height = '100%'
    } else {
      this._container.style.width = window.parent !== window ? `${window.innerWidth}px` : ''
      this._container.style.height = window.parent !== window ? `${window.innerHeight}px` : ''
    }
  }

  /**
   * @param {Event} event
   * @private
   */
  _keyDownEventHandler(event) {
    this.trigger(NekoPlayer.Events.DidKeyDown, this, event)
  }

  /**
   * @param {Event} event
   * @private
   */
  _receiveMessageEventHandler(event) {
    this.trigger(NekoPlayer.Events.DidReceiveMessage, this, event)
  }

  /**
   * @param {Event} event
   * @private
   */
  _disableTouchScroll(event) {
    event.preventDefault()
  }
}

NekoPlayer.Storage = class {
  /**
   * @param {Shaka.offline.Storage} storage
   */
  constructor(storage) {
    this._storage = storage
  }

  static removeAllContent() {
    Shaka.offline.Storage.deleteAll()
  }

  /** @returns {Promise.<!Array.<shaka.extern.StoredContent>>} */
  get contents() { return this._storage.list() }

  /**
   * @param {function(shaka.extern.StoredContent)} term
   * @returns {Promise<shaka.extern.StoredContent>}
   */
  async findContent(term) {
    const contents = await this.contents
    return contents.find(term)
  }
  /**
   * @param {string} uri
   * @returns {Promise<shaka.extern.StoredContent>}
   */
  async contentAtOriginalUri(uri) {
    return await this.findContent(content => content.originalManifestUri === uri)
  }

  /**
   * @async
   * @param {string} uri
   */
  async removeAtOfflineUri(uri) {
    this._storage.remove(uri)
  }

  /**
   * @param {string} uri
   */
  async removeAtOriginalUri(uri) {
    const content = await this.contentAtOriginalUri(uri)
    if (content) {
      this.removeAtOfflineUri(content.offlineUri)
    }
  }
}

NekoPlayer.DataSource = class {
  constructor() {
    /** @private {ArrayBuffer} */
    this._serverCertificate = null
  }

  /**
   * @returns {Uint8Array}
   */
  get serverCertificate() { return new Uint8Array(this._serverCertificate) }

  async init() {
    // override this

    const request = await fetch('/fairplay.cer')
    const certificate = await request.arrayBuffer()
    this._serverCertificate = certificate
  }

  /**
   * プレイヤーのコントローラに利用するカスタム要素を事前登録する
   *
   * @param {NekoPlayer} player
   * @returns {Map<string, Shaka.ui.Element.Factory>}
   */
  registerControlElements(player) {
    // override this

    const elementByName = new Map()
    elementByName.set('multi_view_points_menu', new MultiViewPointsMenu.Factory(this.playSettings, this.drm, this.src, player))
    elementByName.set('manifest_menu', new ManifestMenu.Factory(this.externalManifests))
    elementByName.set('fullscreen_menu_button', new FullscreenMenuButton.Factory((menuButton) => {
      player.isFullscreen ? player.exitFullscreen() : player.enterFullscreen()
      menuButton.toggleFullscreenIcon(player.isFullscreen)
    }))

    return elementByName
  }

  /**
   * @param {NekoPlayer} player
   * @returns {Shaka.ui.SeekBar.Factory}
   */
  registerSeekBarElement(player) {
    // override this

    return new NekoSeekBar.Factory()
  }

  /**
   * @param {NekoPlayer} player
   * @returns {function(shaka.net.NetworkingEngine.RequestType, shaka.extern.Request)}
   */
  registerRequestFilter(player) {
    // override this

    const requestFilter = (type, request) => {
      if (type != Shaka.net.NetworkingEngine.RequestType.LICENSE) {
        return
      }

      // Set appropriate content-type header
      const userAgent = window.navigator.userAgent.toLocaleLowerCase();
      if (userAgent.indexOf('msie') != -1 || userAgent.indexOf('trident') != -1 || userAgent.indexOf('edg') != -1) {
        // For PlayReady
        request.headers['Content-Type'] = 'text/xml; charset=utf-8'
      } else {
        // For FairPlay. Widevine may ignore this
        request.headers['Content-Type'] = 'application/octet-stream'
      }
    }

    return requestFilter
  }

  /**
   * @param {NekoPlayer} player
   * @returns {string} poster image uri
   */
  playerPosterImageUri(player) {
    // override this

    return ''
  }

  /**
   * @async
   * @param {NekoPlayer} player
   * @returns {Promise<string>} 再生するコンテンツのソース(URL やパス)
   */
  async playerContentSrc(player) {
    // override this

    return ''
  }

  /**
   * @param {NekoPlayer} player
   * @returns {Object} https://shaka-player-demo.appspot.com/docs/api/shaka.extern.html#.PlayerConfiguration
   */
  playerConfigration(player) {
    // override this

    return {}
  }

  /**
   * @param {NekoPlayer} player
   * @returns {Object} https://shaka-player-demo.appspot.com/docs/api/shaka.extern.html#.UIConfiguration
   */
  playerUIConfigration(player) {
    // override this

    return {}
  }

  /**
   * @param {NekoPlayer} player
   * @returns {Object} https://shaka-player-demo.appspot.com/docs/api/shaka.extern.html#.OfflineConfiguration
   */
  playerDownloadConfigration(player) {
    // override this

    return { offline: {} }
  }
}

NekoPlayer.Events = {
  WillLoadManifest: Symbol('willLoadManifest'),
  DidLoadManifest: Symbol('didLoadManifest'),
  DidStartPlayback: Symbol('didStartPlayback'),
  DidPausePlayback: Symbol('didPausePlayback'),
  DidBeginSeeking: Symbol('didBeginSeeking'),
  DidEndSeeking: Symbol('didEndSeeking'),
  DidFinishPlayback: Symbol('didFinishPlayback'),
  WillEnterFullscreen: Symbol('willEnterFullscreen'),
  DidEnterFullscreen: Symbol('didEnterFullscreen'),
  DidExitFullscreen: Symbol('didExitFullscreen'),
  DidResizeWindow: Symbol('didResizeWindow'),
  DidRotateWindow: Symbol('didRotateWindow'),
  WillCloseWindow: Symbol('willCloseWindow'),
  DidDownloadProgress: Symbol('didDownloadProgress'),
  DidFinishDownload: Symbol('didFinishDownload'),
  DidFailDownload: Symbol('didFailDownload'),
  DidKeyDown: Symbol('didKeyDown'),
  DidReceiveMessage: Symbol('didReceiveMessage')
}

NekoPlayer.Error = class {
  /**
   * @todo 各エラーと対応するメッセージが確定した後、改修を予定
   * @param {shaka.util.Error} error
   */
  static parse(error) {
    if (error.code === NekoPlayer.Error.Code['BAD_HTTP_STATUS']) {
      const statusCode = error.data[1]
      const messageByStatusCode = {
        400: 'Bad Request',
        401: 'Unauthorized',
        403: 'Forbidden',
        404: 'Not Found',
        408: 'Request Timeout'
      }
      return messageByStatusCode[statusCode] || 'Unknown Error'
    }
    return 'Unknown Error'
  }
}
NekoPlayer.Error.Code = Shaka.util.Error.Code

export default NekoPlayer
